import React, { ComponentType, useCallback, useEffect, useState } from "react";
import JigsawEvent from "../../viewer-context/JigsawEvent";
import useViewer from "../../viewer-context/useViewer";
import useJigsawEvent from "../../viewer-context/useJigsawEvent";
import { useIntl } from "react-intl";
import { isIOS } from "../../../utils/browser";

export interface TTSProps {
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
  skipForward: () => void;
  skipBack: () => void;
  cancel: () => void;
  error: string | null;
  voices: Array<jigsaw.tts.Voice>;
  isReady: boolean;
  canNav: boolean;
  altEnabled: boolean;
  currentVoice: number;
  currentRate: number;
  changeRate: (rate: number) => void;
  setVoice: (index: number) => void;
  enableAltText: () => void;
  disableAltText: () => void;
  isSupported: boolean;
}

export default function withTTS<T extends TTSProps>(
  Component: ComponentType<T>
): ComponentType<Omit<T, keyof TTSProps>> {
  return function ComponentWithTTS(props) {
    const [status, setStatus] = useState<Partial<jigsaw.tts.Status>>({
      isSupported: false,
    });
    const [voices, setVoices] = useState<Array<jigsaw.tts.Voice>>([]);
    const viewer = useViewer().viewer;
    const intl = useIntl();

    const statusUpdate = useCallback((evt: any, data: jigsaw.tts.Status) => {
      setStatus(data);
    }, []);

    useJigsawEvent(JigsawEvent.TTS_STATUS, statusUpdate);

    useEffect(() => {
      if (viewer) {
        viewer.TTS.setNoContentMessage(
          intl.formatMessage({ id: "tts.cantRead" })
        );
        viewer.TTS.sendStatus();
        viewer.TTS.getVoices(setVoices);

        return () => {
          viewer.TTS.cancel();
        };
      }
    }, [viewer, intl]);

    if (!viewer) {
      return null;
    }

    const play = () => {
      if (status.playing) {
        viewer.TTS.resume();
      } else {
        viewer.TTS.startFromTop();
      }
    };

    if (!viewer) {
      return null;
    }

    return (
      <Component
        {...(props as T)}
        isSupported={status.isSupported && !isIOS()}
        isPlaying={status.playing && !status.paused}
        error={status.error}
        voices={voices}
        play={play}
        pause={viewer.TTS.pause}
        skipForward={viewer.TTS.skipForward}
        skipBack={viewer.TTS.skipBack}
        cancel={viewer.TTS.cancel}
        isReady={status.isReady}
        canNav={status.canNav}
        currentVoice={status.voice}
        altEnabled={status.altEnabled}
        currentRate={status.rate}
        changeRate={viewer.TTS.changeRate}
        setVoice={viewer.TTS.setVoice}
        enableAltText={viewer.TTS.enableAltText}
        disableAltText={viewer.TTS.disableAltText}
      />
    );
  };
}
